import React, { useEffect, useState } from 'react';
import {
  Row, Col, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, Input, Label, FormFeedback, ButtonGroup, UncontrolledDropdown,
  DropdownToggle, DropdownMenu, DropdownItem, Spinner
} from "reactstrap";
import { useFormik } from 'formik';
import * as Yup from "yup";
import classnames from "classnames";
import DualListBox from "react-dual-listbox";
import "react-dual-listbox/lib/react-dual-listbox.css";
import { addBrand, getAllBillings, updateBrand } from '../../../../slices/auth/settings/thunk';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { setBrandModalData, toggleBrandModal } from '../../../../slices/modals/reducer';
import CustomSelect from '../../../../Components/Common/CustomSelect';
import country from '../../../../common/country';
import UploadLogo from '../../../../Components/Common/UploadLogo';
import ErrorAlert from '../../../../Components/Common/ErrorAlert';
import { resetBrandErr } from '../../../../slices/auth/settings/reducer';
import Preloader from '../../../../Components/Common/Preloader';
import SimpleBar from 'simplebar-react';
import CustomSelectWithSearch from '../../../../Components/Common/CustomSelectWithSearch';

/**
 * React component for managing brand information in a modal.
 * @component
 */
function BrandModal() {
  const dispatch = useDispatch();

  const selectBrandModal = createSelector((state) => state, (state) => ({
    brandModal: state.Modal.brandModal,
    brandModalType: state.Modal.brandModalType,
    brandModalData: state.Modal.brandModalData,
    brandErr: state.Settings.brandErr,
    brandErrMsg: state.Settings.brandErrMsg,
    billing: state.Settings.billing,
    client: state.MenuData.client,
    brandPath: state.MenuData.brandPath,
    clientPath: state.MenuData.clientPath,
  }));
  const { brandModal, brandModalType, brandModalData, brandErr, brandErrMsg, billing, client, brandPath, clientPath } = useSelector(selectBrandModal);

  // Billing
  const [formData, setFormData] = useState(null);
  const [invoicesData, setInvoicesData] = useState([]);
  const options = country.map(item => { return { value: item.name, label: item.name } });
  const [selectedOpt, setSelectedOpt] = useState([]);
  const [parentBilling, setParentBilling] = useState(null);
  const [useInvoice, setUseInvoice] = useState(false);

  useEffect(() => {
    if (brandModalData !== null) {
      fillModal(brandModalData);
      setFormData(brandModalData);
      const foundClient = client.clients.find(item => item._id === brandModalData.clientId);
      const foundBrand = foundClient.brands.find(item => item._id === brandModalData._id);
      const markets = foundBrand.markets.map(item => { return { label: item.name, value: item.name } })
      setSelectedOpt(markets);
    }
  }, [brandModalData]);

  useEffect(() => {
    if (billing) {
      try {
        setInvoicesData(billing);
        const foundClient = client.clients.find(item => item._id === clientPath);
        const foundParentBilling = billing.find(item => item._id === foundClient.billingId);
        if (foundParentBilling) {
          setParentBilling(foundParentBilling);
        }
      } catch (error) {

      }
    } else {
      setInvoicesData([]);
    }
  }, [billing]);

  useEffect(() => {
    if (formData && Array.isArray(invoicesData) && invoicesData.length > 0) {
      const foundBilling = invoicesData.find(item => item._id === formData.billingId);
      if (foundBilling) {
        fillInvoiceDetails(foundBilling);
      }
    }
  }, [formData, invoicesData]);

  useEffect(() => {
    validation.setValues({
      ...validation.values,
      markets: selectedOpt
    })
  }, [selectedOpt])

  const selectClientId = createSelector(state => state.MenuData, state => state.clientPath);
  const clientId = useSelector(selectClientId);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      logo: "",
      name: "",
      webSite: "",
      billingId: null,
      billingName: "",
      markets: [],
      taxNumber: "",
      billingAddress: "",
      billingCountry: '',
      phoneNumber: "",
    },
    validationSchema: Yup.object({
      // logo: Yup.string().required("Please enter client name"),
      name: Yup.string().required("Please enter client name"),
      webSite: Yup.string().required('The website is required'),
      billingName: Yup.string().required("Please enter billing name"),
      taxNumber: Yup.string().required("Please enter a tax number"),
      billingAddress: Yup.string().required("Please enter billing address"),
      billingCountry: Yup.string().required('Please select billing country'),
      phoneNumber: Yup.string().required("Please enter a phone number"),
    }),
    onSubmit: async (values) => {
      if (brandModalType === "add") {
        const brandCreated = await dispatch(addBrand({ ...values, clientId: clientId }));
        if (brandCreated) {
          closeModal();
        }
      } else if (brandModalType === "edit") {
        const brandUpdated = await dispatch(updateBrand({ ...values, clientId: clientId }));
        if (brandUpdated) {
          closeModal();
        }
      }
    }
  });

  /**
   * Fills a modal with the provided data using Formik validation.
   * @param {Object} data - The data to be used for filling the modal.
   */
  function fillModal(data) {
    validation.setValues({ ...data });
    setSelectedOpt(data.markets);
  }

  /**
   * Handles a change in the form field value and updates Formik validation accordingly.
   * @param {string} key - The key of the form field to be updated.
   * @param {any} value - The new value for the form field.
   */
  function handleChange(key, item) {
    validation.setFieldValue(key, item);
  }

  /**
   * Clears the form by resetting Formik validation, updating brand modal data, and resetting selected options and the "use company" flag.     *
   */
  function clearForm() {
    validation.resetForm();
    setSelectedOpt([]);
    dispatch(setBrandModalData(null));
    setParentBilling(null);
    setInvoicesData([]);
    setFormData(null);
    setUseInvoice(false);
  }

  function fillInvoiceDetails(billing) {
    validation.setValues({
      ...validation.values,
      billingId: billing._id,
      billingName: billing.billingName,
      taxNumber: billing.taxNumber,
      billingAddress: billing.billingAddress,
      billingCountry: billing.billingCountry,
      phoneNumber: billing.phoneNumber
    });
    validation.setTouched({
      ...validation.touched,
      billingId: false,
      billingName: false,
      taxNumber: false,
      billingAddress: false,
      billingCountry: false,
      phoneNumber: false
    });
  }

  /**
   * Toggles the use of company information for invoicing in the form and updates Formik validation accordingly.
   */
  function toggleCompanyForInvoicing() {
    if (!useInvoice && parentBilling) {
      setUseInvoice(true);
      fillInvoiceDetails(parentBilling)
    } else {
      setUseInvoice(false);
      validation.setValues({
        ...validation.values,
        billingId: null,
        billingName: "",
        taxNumber: "",
        billingAddress: "",
        billingCountry: "",
        phoneNumber: ""
      })
    }
  }

  function closeModal() {
    dispatch(toggleBrandModal());
    setTimeout(() => {
      clearForm();
    }, 200);
  }
  return (
    <Modal isOpen={brandModal} toggle={closeModal} centered size='lg'>
      <ModalHeader className="p-3" toggle={closeModal}>
        {(brandModalType === "add" ? "Add" : "Edit")} Brand
      </ModalHeader>
      <Form className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}>
        <ModalBody>
          {Array.isArray(invoicesData) && invoicesData.length > 0 ?
            <React.Fragment>
              <Row>
                <Col>
                  <div className="text-center pb-4">
                    <UploadLogo size='lg'
                      src={validation.values.logo}
                      onChange={(logo) => { validation.setValues({ ...validation.values, logo: logo }) }} />
                    <p className="mb-0 fw-normal">Brand Logo</p>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <ErrorAlert errorState={brandErr} errorMessage={brandErrMsg} resetError={resetBrandErr} />
                </Col>
              </Row>

              <Row>
                <Col>
                  <div id="modal-id">
                    <div className='w-100 d-flex justify-content-between'>
                      <Label htmlFor="name" className="form-label">Brand Name</Label>
                      {validation.touched.name && !validation.errors.name ? (
                        <FormFeedback valid>Looks good</FormFeedback>
                      ) : null}
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                      ) : null}
                    </div>
                    <Input type="text" id="name" name="name"
                      placeholder="Please enter brand name"
                      className={"form-control " + classnames({ "is-valid": validation.touched.name && !validation.errors.name })}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.name && validation.errors.name ? true : false}
                      value={validation.values.name || ""} />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="mt-3" id="modal-id">
                    <div className='w-100 d-flex justify-content-between'>
                      <Label htmlFor="webSite" className="form-label">Website</Label>
                      {validation.touched.webSite && !validation.errors.webSite ? (
                        <FormFeedback valid>Looks good</FormFeedback>
                      ) : null}
                      {validation.touched.webSite && validation.errors.webSite ? (
                        <FormFeedback type="invalid">{validation.errors.webSite}</FormFeedback>
                      ) : null}
                    </div>
                    <Input type="text" id="webSite" name="webSite"
                      placeholder="www.example.com"
                      className={"form-control " + classnames({ "is-valid": validation.touched.webSite && !validation.errors.webSite })}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.webSite && validation.errors.webSite ? true : false}
                      value={validation.values.webSite || ""} />
                  </div>
                </Col>
              </Row>
              {brandModalType === "add" ?
                <Row>
                  <h5 className='my-4'>Select Markets</h5>
                  <Col>
                    <label htmlFor='markets'>Markets</label>
                    <DualListBox
                      id="market"
                      canFilter
                      filterCallback={(options, filterInput) => {
                        if (filterInput === "") {
                          return true;
                        }
                        return new RegExp(filterInput, "i").test(options.label);
                      }}
                      filterPlaceholder="Search..."
                      options={options}
                      selected={selectedOpt}
                      onChange={(e) => setSelectedOpt(e)}
                      icons={{
                        moveLeft: <span className="mdi mdi-chevron-left" key="key" />,
                        moveRight: <span className="mdi mdi-chevron-right" key="key" />,
                      }}
                    />
                  </Col>
                </Row>
                : null}
              <Row>
                <Col xxl="12" xl="12" lg="12" md="12" sm="12" className='d-flex justify-content-between align-items-center'>
                  <h5 className='my-4'>Invoicing Details</h5>
                  <ButtonGroup>
                    <UncontrolledDropdown direction='end'>
                      <DropdownToggle tag="span" className='btn btn-icon bg-light text-black border border-0'>
                        <i className="ri-list-unordered fs-20"></i>
                      </DropdownToggle>
                      <DropdownMenu className='dropdown-menu-md'>
                        <div className='dropdown-header noti-title border-bottom mb-2'>
                          <h6 className='fs-12'>List of invoice accounts</h6>
                        </div>
                        <SimpleBar style={{ width: "100%", maxHeight: "220px", overflowX: "hidden" }}>
                          {(invoicesData || []).map((item, index) => {
                            return (
                              <DropdownItem
                                key={index}
                                className='py-2 fs-12 border-bottom'
                                onClick={() => {
                                  fillInvoiceDetails(item)
                                }}>
                                <p className='mb-2 fw-bold'>{(item.billingName || "")}</p>
                                <p className='text-wrap my-0'>
                                  <b>Tax Number:</b> {(item.taxNumber || "")}
                                </p>
                                <p className='text-wrap my-0'>
                                  <b>Billing Address:</b> {(item.billingAddress || "")}
                                </p>
                                <p className='text-wrap my-0'>
                                  <b>Billing Country:</b> {(item.billingCountry || "")}
                                </p>
                              </DropdownItem>
                            )
                          })}
                        </SimpleBar>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </ButtonGroup>
                </Col>
                <Col sm="12" md="6">
                  <div className='mt-3'>
                    <div className='w-100 d-flex justify-content-between'>
                      <Label htmlFor="billingName" className="form-label">Billing Name</Label>
                      {validation.touched.billingName && !validation.errors.billingName ? (
                        <FormFeedback valid>Looks good</FormFeedback>
                      ) : null}
                      {validation.touched.billingName && validation.errors.billingName ? (
                        <FormFeedback type="invalid">{validation.errors.billingName}</FormFeedback>
                      ) : null}
                    </div>
                    <Input type="text" id="billngName" name="billingName"
                      placeholder="Please enter billing name"
                      className={"form-control " + classnames({ "is-valid": validation.touched.billingName && !validation.errors.billingName })}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.billingName && validation.errors.billingName ? true : false}
                      value={validation.values.billingName || ""} disabled={useInvoice} />
                  </div>
                </Col>
                <Col sm="12" md="6">
                  <div className='mt-3'>
                    <div className='w-100 d-flex justify-content-between'>
                      <Label htmlFor="taxNumber" className="form-label">Tax Number</Label>
                      {validation.touched.taxNumber && !validation.errors.taxNumber ? (
                        <FormFeedback valid>Looks good</FormFeedback>
                      ) : null}
                      {validation.touched.taxNumber && validation.errors.taxNumber ? (
                        <FormFeedback type="invalid">{validation.errors.taxNumber}</FormFeedback>
                      ) : null}
                    </div>
                    <Input type="text" id="taxNumber" name="taxNumber"
                      placeholder="Please enter tax number"
                      className={"form-control " + classnames({ "is-valid": validation.touched.taxNumber && !validation.errors.taxNumber })}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.taxNumber && validation.errors.taxNumber ? true : false}
                      value={validation.values.taxNumber || ""} disabled={useInvoice} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col sm="12" md="6">
                  <div className='mt-3'>
                    <div className='w-100 d-flex justify-content-between'>
                      <Label htmlFor='billingAddress'>Billing Address</Label>
                      {validation.touched.billingAddress && !validation.errors.billingAddress ? (
                        <FormFeedback valid>Looks good</FormFeedback>
                      ) : null}
                      {validation.touched.billingAddress && validation.errors.billingAddress ? (
                        <FormFeedback type="invalid">{validation.errors.billingAddress}</FormFeedback>
                      ) : null}
                    </div>
                    <Input
                      type="textarea"
                      className={'form-control ' + classnames({ "is-valid": validation.touched.billingAddress && !validation.errors.billingAddress })}
                      name='billingAddress'
                      id='billingAddress'
                      rows="5"
                      placeholder='Please enter clients billing address'
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.billingAddress && validation.errors.billingAddress ? true : false}
                      value={validation.values.billingAddress || ""}
                      disabled={useInvoice}
                    ></Input>
                  </div>
                </Col>
                <Col sm="12" md="6">
                  <Row>
                    <Col lg={12} sm={12} md={12}>
                      <div className='mt-3'>
                        <div className='w-100 d-flex justify-content-between'>
                          <Label htmlFor='billingCountry'>Billing Country</Label>
                          {validation.touched.billingCountry && !validation.errors.billingCountry ? (
                            <FormFeedback valid>Looks good</FormFeedback>
                          ) : null}
                          {validation.touched.billingCountry && validation.errors.billingCountry ? (
                            <FormFeedback type="invalid">{validation.errors.billingCountry}</FormFeedback>
                          ) : null}
                        </div>
                        <CustomSelectWithSearch
                          className={"form-select"}
                          placeholder='Select Country'
                          validation={classnames({ "is-valid": validation.touched.billingCountry && !validation.errors.billingCountry, "is-invalid": validation.touched.billingCountry && validation.errors.billingCountry })}
                          options={country}
                          onChange={(item) => handleChange("billingCountry", item.name)}
                          value={validation.values.billingCountry || ''}
                          disabled={useInvoice}
                        />
                      </div>
                    </Col>
                    <Col lg={12} sm={12} md={12}>
                      <div className='mt-3'>
                        <div className='w-100 d-flex justify-content-between'>
                          <Label htmlFor="phoneNumber" className="form-label">Phone Number</Label>
                          {validation.touched.phoneNumber && !validation.errors.phoneNumber ? (
                            <FormFeedback valid>Looks good</FormFeedback>
                          ) : null}
                          {validation.touched.phoneNumber && validation.errors.phoneNumber ? (
                            <FormFeedback type="invalid">{validation.errors.phoneNumber}</FormFeedback>
                          ) : null}
                        </div>
                        <Input type="text" id="phoneNumber" name="phoneNumber"
                          placeholder="Please enter phone number"
                          className={"form-control " + classnames({ "is-valid": validation.touched.phoneNumber && !validation.errors.phoneNumber })}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={validation.touched.phoneNumber && validation.errors.phoneNumber ? true : false}
                          value={validation.values.phoneNumber || ""} disabled={useInvoice} />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </React.Fragment>
            : <Preloader isComponentLoader={true} />}
        </ModalBody>
        <ModalFooter className='d-flex justify-content-between'>
          <div className='d-flex justify-self-start'>
            {parentBilling &&
              <React.Fragment>
                <Input className="form-check-input" type="checkbox" onClick={toggleCompanyForInvoicing} value="" id="invoice-check" />
                <Label className="mx-1 fw-normal form-check-label" htmlFor="invoice-check">Use <b>{parentBilling.billingName}</b> for invoicing</Label>
              </React.Fragment>
            }
          </div>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
            {Array.isArray(invoicesData) && invoicesData.length > 0 &&
              <button type="submit" className="btn btn-success" id="add-btn">
                {(brandModalType === "add" ? "Add" : "Save")} Brand
              </button>
            }
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default BrandModal
